/**
 * 平台抽象层
 * ------------------------------------------------------------
 * 收敛 frontend 的运行时环境差异。
 *
 * 本目录只服务 Web：真正的移动端是 app_flutter/ 与 hmos/，各自有独立的
 * HTTP 栈与令牌存储。此前这里保留过一整套 Capacitor 原生壳分支
 * （isNativePlatform / NativeTokenStorage / X-Client-Platform: native），
 * 但仓库从未有 mobile/ 子项目、package.json 也从未安装任何 @capacitor/* 依赖，
 * isNativePlatform() 恒为 false，那些分支永远走不到；其中动态加载
 * @capacitor/preferences 还得靠 `new Function('m','return import(m)')`
 * 绕过静态分析——等于为一段死代码在页面里开了个 eval 口子（会被 CSP 拦，
 * 也是扫描器必报项）。故整体移除，只留 Web 真正用到的基址解析。
 */

/**
 * API 基址：读 VITE_API_BASE_URL（默认空串 → 走同源 / vite 代理的相对路径）。
 * 同步返回，供 client.ts 构建 ofetch 实例与 useSiteConfig 拼静态资源 origin 使用。
 */
export function getApiBaseURL(): string {
  const web = import.meta.env.VITE_API_BASE_URL as string | undefined;
  return web || '';
}

/**
 * 实时通知通道的连接地址。
 *
 * `path` 取自 `POST /notifications/socket-ticket` 的返回值（当前是 `/api/ws`），
 * 由服务端下发而非前端硬编码：它是全局前缀 + 网关路径的合成结果，改任何一端
 * 都不该指望另一端同步改字面量。因为 path 已含 `/api`，这里只取 API 基址的
 * origin 部分，不再叠加它的 pathname，否则会拼出 `/api/api/ws`。
 *
 * 同源部署时结果是当前页的 origin，dev 下由 vite 的 `/api` 代理（需 ws:true）转发；
 * 基址配成绝对 URL 时则直连该主机。
 *
 * 地址里没有任何凭证：浏览器发起 WebSocket 握手时带不上自定义请求头，令牌唯一可能的
 * 落点就是查询串，而 accessToken 进 URL 会落进代理日志、浏览器历史并随 Referer 外泄。
 * 故改为先用 Authorization 头换一张一次性票据，连上后经第一帧交出（见后端 socket-ticket.service.ts）。
 */
export function getSocketURL(path: string): string {
  const base = getApiBaseURL();
  const origin = base
    ? new URL(base, window.location.origin).origin
    : window.location.origin;
  const url = new URL(path, origin);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return url.toString();
}
